'use client'

import { cartService } from '@/domain/Cart'
import { discountService } from '@/domain/Discount'
import { pushEcommerceEvent } from '@/lib/gtm'
import CartService from '@/services/Cart/Cart.service'
import eventEmitter from '@/utils/event-emitter'
import { LoadingOverlay } from '@mantine/core'
import { Cart, Region } from '@medusajs/medusa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { z } from 'zod'

interface VariantInfoProps {
    variantId: string
}

interface LineInfoProps {
    lineId: string
}

interface StoreContext {
    cart: Cart | null
    totalItems: number
    countryCode: string | undefined
    setRegion: (regionId: string, countryCode: string) => void
    addItem: (item: VariantInfoProps & { silent?: boolean }) => void
    updateItem: (item: LineInfoProps) => void
    deleteItem: (lineId: string) => void
    resetCart: () => void
}

const StoreContext = React.createContext<StoreContext | null>(null)

export const useStore = () => {
    const context = React.useContext(StoreContext)
    if (context === null) {
        throw new Error('useStore must be used within a StoreProvider')
    }
    return context
}

interface StoreProps {
    children: React.ReactNode
}

const IS_SERVER = typeof window === 'undefined'
const CART_KEY = 'medusa_cart_id'
const REGION_KEY = 'medusa_region'

export const StoreProvider = ({ children }: StoreProps) => {
    const [cart, setCart] = useState<Cart | null>(null)
    const [countryCode, setCountryCode] = useState<string | undefined>(
        undefined,
    )

    const createCartMutation = useMutation({
        mutationFn: (dto: { region_id?: string }) =>
            CartService.createCart(dto),
    })

    const updateCartMutation = useMutation({
        mutationFn: (dto: { cart_id: string; region_id?: string }) =>
            CartService.updateCart(dto),
    })

    const addLineItem = useMutation({
        mutationFn: (dto: {
            cart_id: string
            variant_id: string
            quantity: number
        }) => CartService.addLineItem(dto),
    })

    const removeLineItem = useMutation({
        mutationFn: (dto: { cart_id: string; line_id: string }) =>
            CartService.deleteLineItem(dto),
    })

    const adjustLineItem = useMutation({
        mutationFn: (dto: {
            cart_id: string
            line_id: string
            quantity: number
        }) => CartService.updateLineItem(dto),
    })

    // check if the user is onboarding and sets the onboarding session storage
    const searchParams = useSearchParams()
    const onboardingCartId = searchParams.get('cart_id')
    const isOnboarding = searchParams.get('onboarding')

    useEffect(() => {
        if (isOnboarding === 'true') {
            sessionStorage.setItem('onboarding', 'true')
        }
    }, [isOnboarding])

    const storeRegion = (regionId: string, countryCode: string) => {
        if (!IS_SERVER) {
            localStorage.setItem(
                REGION_KEY,
                JSON.stringify({ regionId, countryCode }),
            )

            setCountryCode(countryCode)
        }
    }

    useEffect(() => {
        if (!IS_SERVER) {
            const storedRegion = localStorage.getItem(REGION_KEY)
            if (storedRegion) {
                const { countryCode } = JSON.parse(storedRegion)
                setCountryCode(countryCode)
            }
        }
    }, [])

    const getRegion = () => {
        if (!IS_SERVER) {
            const region = localStorage.getItem(REGION_KEY)
            if (region) {
                return JSON.parse(region) as {
                    regionId: string
                    countryCode: string
                }
            }
        }
        return null
    }

    const setRegion = async (regionId: string, countryCode: string) => {
        if (!cart) return
        try {
            const updatedCart = await updateCartMutation.mutateAsync({
                cart_id: cart.id,
                region_id: regionId,
            })
            setCart(updatedCart)
            storeCart(updatedCart.id)
            storeRegion(regionId, countryCode)
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error(error)
            }
        }
    }

    const ensureRegion = (region: Region, countryCode?: string | null) => {
        if (!IS_SERVER) {
            const { regionId, countryCode: defaultCountryCode } =
                getRegion() || {
                    regionId: region.id,
                    countryCode: region.countries[0].iso_2,
                }

            const finalCountryCode = countryCode || defaultCountryCode

            if (regionId !== region.id) {
                setRegion(region.id, finalCountryCode)
            }

            storeRegion(region.id, finalCountryCode)
            setCountryCode(finalCountryCode)
        }
    }

    const storeCart = (id: string) => {
        if (!IS_SERVER) {
            localStorage.setItem(CART_KEY, id)
        }
    }

    const getStoredCartId = () => {
        if (!IS_SERVER) {
            return localStorage.getItem(CART_KEY)
        }
        return null
    }

    const deleteStoredCart = () => {
        if (!IS_SERVER) {
            localStorage.removeItem(CART_KEY)
        }
    }

    const deleteRegion = () => {
        if (!IS_SERVER) {
            localStorage.removeItem(REGION_KEY)
        }
    }

    const createNewCart = async (regionId?: string) => {
        try {
            const newCart = await createCartMutation.mutateAsync({
                region_id: regionId,
            })
            setCart(newCart)
            storeCart(newCart.id)
            ensureRegion(newCart.region, newCart.shipping_address?.country_code)
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error(error)
            }
        }
    }

    const resetCart = async () => {
        // Supprimer le code promo côté back si présent avant de vider le panier
        if (cart?.discounts?.length && cart.id) {
            const discountCode = cart.discounts[0].code
            try {
                await discountService.remove({
                    cart_id: cart.id,
                    code: discountCode,
                })
            } catch {
                // On continue même si la suppression échoue
            }
        }

        deleteStoredCart()

        setCart(null)

        // 2. Vider le cache React Query (les deux en parallèle)
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['cart'] }),
            queryClient.removeQueries({ queryKey: ['cart'] }),
        ])
    
        const savedRegion = getRegion()

        try {
            const newCart = await createCartMutation.mutateAsync({
                region_id: savedRegion?.regionId,
            })
            setCart(newCart)
            storeCart(newCart.id)
            ensureRegion(newCart.region, newCart.shipping_address?.country_code)
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error(error)
            }
        }
    }

    useEffect(() => {
        const ensureCart = async () => {
            const cartId = onboardingCartId || getStoredCartId()
            const region = getRegion()

            if (cartId) {
                const cartRes = await cartService
                    .getCartById({ cart_id: cartId })
                    .then((cart) => {
                        return cart
                    })
                    .catch(async (_) => {
                        return null
                    })

                if (!cartRes || cartRes.completed_at) {
                    deleteStoredCart()
                    deleteRegion()
                    await createNewCart()
                    return
                }

                setCart(cartRes)
                ensureRegion(cartRes.region)
            } else {
                await createNewCart(region?.regionId)
            }
        }

        if (!IS_SERVER && !cart?.id) {
            ensureCart()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.id])

    const queryClient = useQueryClient()

    const [silentAdding, setSilentAdding] = useState(false)

    const addItem = async ({ variantId, silent = true }: { variantId: string; silent?: boolean }) => {
        if (!cart) return
        setSilentAdding(!!silent)

        // Supprimer le code promo côté back si présent avant d'ajouter l'article
        if (cart.discounts?.length) {
            const discountCode = cart.discounts[0].code
            try {
                await discountService.remove({
                    cart_id: cart.id,
                    code: discountCode,
                })
            } catch {
                // On continue même si la suppression échoue
            }
        }

        addLineItem.mutate(
            {
                cart_id: cart.id,
                variant_id: variantId,
                quantity: 1,
            },
            {
                onSuccess: (updatedCart) => {
                    setCart(updatedCart)
                    storeCart(updatedCart.id)
                    if (!silent) eventEmitter.emit('TOGGLE_CART_DRAWER')

                    eventEmitter.emit('FLY_TO_CART', variantId)

                    const addedLine = updatedCart.items?.find(
                        (item) => item.variant_id === variantId,
                    )
                    
                    if ( addedLine )
                    {
                        const product = addedLine.variant.product as typeof addedLine.variant.product & {
                            template_barcode: string
                        }

                        pushEcommerceEvent('add_to_cart', {
                            currency: updatedCart.region?.currency_code?.toUpperCase() ?? 'EUR',
                            value: addedLine ? addedLine.unit_price / 100 : 0,
                            items: [{
                                item_id: product.template_barcode,
                                item_name: addedLine.variant.product.title,
                                price: addedLine ? addedLine.unit_price / 100 : 0,
                                quantity: 1,
                            }]
                        })
                    } 
                },

                onError: async (error) => {
                    if (axios.isAxiosError(error)) {
                        if (error.response?.status === 400) {
                            // This means that we have an issue with the variant

                            const ErrorSchema = z.object({
                                code: z.string(),
                                message: z.string(),
                                type: z.string(),
                            })

                            const validatedError = ErrorSchema.safeParse(
                                error.response?.data,
                            )

                            if (!validatedError.success) {
                                return
                            }

                            if (
                                validatedError.data.code ===
                                'insufficient_inventory'
                            ) {
                                toast.error(
                                    "Il n'y a plus de stock disponible pour ce produit",
                                )
                            }
                            await queryClient.invalidateQueries()
                            window.location.reload()
                        }
                    }
                },
            },
        )
    }

    const deleteItem = (lineId: string) => {
        if (!cart) return

        const removedLine = cart.items?.find((item) => item.id === lineId)


        removeLineItem.mutate(
            {
                cart_id: cart.id,
                line_id: lineId,
            },
            {
                onSuccess: (updatedCart) => {
                    setCart(updatedCart)
                    storeCart(updatedCart.id)
                    if (removedLine) {

                          const product = removedLine.variant.product as typeof removedLine.variant.product & {
                            template_barcode: string
                        }

                        pushEcommerceEvent('remove_from_cart', {
                        currency: updatedCart.region?.currency_code?.toUpperCase() ?? 'EUR',
                        value: removedLine.unit_price / 100,
                        items: [{
                            item_id: product.template_barcode,
                            item_name: removedLine.variant.product.title,
                            price: removedLine.unit_price / 100,
                            quantity: removedLine.quantity,
                        }]
                    })
                }
                },
                onError: (error) => {},
            },
        )
    }

    const updateItem = ({ lineId }: { lineId: string }) => {
        if (!cart) return
        adjustLineItem.mutate(
            {
                cart_id: cart.id,
                line_id: lineId,
                quantity: 1,
            },
            {
                onSuccess: (updatedCart) => {
                    setCart(updatedCart)
                    storeCart(updatedCart.id)
                },
                onError: (error) => {},
            },
        )
    }

    return (
        <StoreContext.Provider
            value={{
                cart,
                totalItems: cart?.items?.length ?? 0,
                countryCode,
                setRegion,
                addItem,
                deleteItem,
                updateItem,
                resetCart,
            }}
        >
           
            {children}
        </StoreContext.Provider>
    )
}
